import React from "react";
import { createPortal } from "react-dom";
import { ArrowPathIcon, CloudArrowDownIcon } from "@heroicons/react/24/outline";
import { ActionButton } from "./ResultsPrimitives.jsx";
import {
  createDeploymentUpdateController,
  readLoadedDeploymentAssetVersion,
  subscribeToDeploymentUpdateSignals,
} from "../lib/deploymentUpdateRuntime.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  readDeploymentUpdateConfig,
  shouldShowDeploymentUpdateGuard,
  subscribeDeploymentUpdateConfig,
  watchDeploymentUpdateConfig,
} from "../lib/deploymentUpdateConfig.js";

const INITIAL_GUARD_STATE = {
  phase: "monitoring",
  targetVersion: "",
  countdownSeconds: 5,
};

function getBrowserSessionStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function resolveGuardCopy(state) {
  if (state.phase === "countdown") {
    return {
      eyebrow: "Nouvelle version prête",
      title: "Mise à jour du SaaS",
      message: `L'application sera actualisée automatiquement dans ${state.countdownSeconds} seconde${state.countdownSeconds > 1 ? "s" : ""}.`,
    };
  }
  if (state.phase === "delayed") {
    return {
      eyebrow: "Mise à jour prolongée",
      title: "Le service termine sa préparation",
      message: "La nouvelle version prend plus de temps que prévu à devenir disponible. Vous pouvez relancer la vérification sans quitter cette page.",
    };
  }
  if (state.phase === "manual") {
    return {
      eyebrow: "Actualisation requise",
      title: "Nouvelle version non chargée",
      message: "La page utilise encore une ancienne version. Actualisez-la maintenant pour continuer, sans risque de boucle automatique.",
    };
  }
  if (state.phase === "reloading") {
    return {
      eyebrow: "Actualisation",
      title: "Chargement de la nouvelle version",
      message: "Votre page et votre session sont conservées pendant l'actualisation.",
    };
  }
  return {
    eyebrow: "Mise à jour détectée",
    title: "Préparation de la nouvelle version",
    message: "Nous attendons que le service soit entièrement prêt avant d'actualiser cette page.",
  };
}

export default function DeploymentUpdateGuard() {
  const config = React.useSyncExternalStore(
    subscribeDeploymentUpdateConfig,
    readDeploymentUpdateConfig,
    readDeploymentUpdateConfig,
  );
  React.useEffect(() => watchDeploymentUpdateConfig(), []);
  if (!config.initialized || (!config.autoReloadRequired && !config.bannerEnabled)) {
    return null;
  }
  return <DeploymentUpdateMonitor bannerEnabled={config.bannerEnabled} />;
}

function DeploymentUpdateMonitor({ bannerEnabled }) {
  const controllerRef = React.useRef(null);
  const [guardState, setGuardState] = React.useState(INITIAL_GUARD_STATE);

  React.useLayoutEffect(() => {
    const controller = createDeploymentUpdateController({
      loadedVersion: readLoadedDeploymentAssetVersion(),
      reload: () => window.location.reload(),
      storage: getBrowserSessionStorage(),
    });
    controllerRef.current = controller;

    const unsubscribeState = controller.subscribe(setGuardState);
    const unsubscribeSignals = subscribeToDeploymentUpdateSignals((signal) => {
      if (signal?.type === "version") {
        // Confirme une réponse métier éventuellement tardive auprès du serveur courant.
        void controller.requestVersionCheck(signal.source);
        return;
      }
      if (signal?.type === "check") {
        void controller.requestVersionCheck(signal.reason);
      }
    });
    const handleVisibilityChange = () => {
      controller.setVisible(document.visibilityState !== "hidden");
    };
    const requestImmediateCheck = () => {
      void controller.requestVersionCheck("browser_event");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", requestImmediateCheck);
    window.addEventListener("online", requestImmediateCheck);
    handleVisibilityChange();
    controller.start();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", requestImmediateCheck);
      window.removeEventListener("online", requestImmediateCheck);
      unsubscribeSignals();
      unsubscribeState();
      controller.dispose();
      controllerRef.current = null;
    };
  }, []);

  if (!shouldShowDeploymentUpdateGuard(guardState.phase, bannerEnabled)) {
    return null;
  }

  const copy = resolveGuardCopy(guardState);
  const isWaiting = guardState.phase === "waiting_ready" || guardState.phase === "reloading";
  const guard = (
    <div
      aria-labelledby="deployment-update-title"
      aria-modal="true"
      className="fixed inset-0 z-[10050] grid place-items-center bg-slate-950/48 px-4 py-6 font-sans backdrop-blur-sm"
      data-deployment-update-guard={guardState.phase}
      role="alertdialog"
    >
      <section className="w-full max-w-xl overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.32)]">
        <div className="border-b border-line-soft bg-[linear-gradient(135deg,rgba(237,246,255,0.98),rgba(240,253,248,0.98))] px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-accent shadow-[0_12px_30px_rgba(15,23,42,0.12)]">
              {isWaiting ? (
                <ArrowPathIcon aria-hidden="true" className="h-6 w-6 animate-spin" />
              ) : (
                <CloudArrowDownIcon aria-hidden="true" className="h-6 w-6" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[0.68rem] font-black uppercase tracking-[0.18em] text-accent">
                {copy.eyebrow}
              </p>
              <h1 id="deployment-update-title" className="mt-1.5 text-xl font-semibold tracking-[-0.03em] text-text">
                {copy.title}
              </h1>
            </div>
          </div>
        </div>

        <div className="grid gap-5 px-6 py-6">
          <p aria-live="polite" className="text-sm leading-7 text-muted-strong">
            {copy.message}
          </p>

          {guardState.phase === "countdown" ? (
            <ActionButton
              className="w-full justify-center px-5!"
              onClick={() => controllerRef.current?.reloadNow()}
              size="md"
              tone="optimize"
            >
              Actualiser maintenant
            </ActionButton>
          ) : null}

          {guardState.phase === "delayed" ? (
            <ActionButton
              className="w-full justify-center px-5!"
              onClick={() => controllerRef.current?.retryReadiness()}
              size="md"
              tone="optimize"
            >
              Vérifier de nouveau
            </ActionButton>
          ) : null}

          {guardState.phase === "manual" ? (
            <ActionButton
              className="w-full justify-center px-5!"
              onClick={() => controllerRef.current?.reloadNow()}
              size="md"
              tone="optimize"
            >
              Actualiser l&apos;application
            </ActionButton>
          ) : null}
        </div>
      </section>
    </div>
  );
  const portalRoot = getReactPortalRoot();

  return portalRoot instanceof HTMLElement ? createPortal(guard, portalRoot) : guard;
}
